import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js'
import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { createBrowserHistory } from 'history'

/* global Office */

export const reactPlugin = new ReactPlugin()
const clickPluginInstance = new ClickAnalyticsPlugin()
const clickPluginConfig = { autoCapture: true }
let AppInsightsInstance

const init = (connectionString, appVersion, buildNumber, enableNavigationTracking = true) => {
  if (import.meta.env.PROD === true) {
    // Configure extensions based on whether navigation tracking is enabled
    const extensions = [clickPluginInstance]
    const extensionConfig = {
      [clickPluginInstance.identifier]: clickPluginConfig
    }

    if (enableNavigationTracking) {
      // Only create history object if navigation tracking is enabled
      let history
      try {
        // Check if we're in an Office.js environment before using browser history
        if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox) {
          console.warn(
            'Office.js detected, navigation tracking may not work properly. Consider disabling navigation tracking for Outlook add-ins.'
          )
          // Still try to create browser history, but warn the user
        }
        history = createBrowserHistory()
      } catch (error) {
        console.warn('Failed to create history object, navigation tracking disabled:', error)
        // If history creation fails, disable navigation tracking
        enableNavigationTracking = false
      }

      if (enableNavigationTracking) {
        // Add ReactPlugin for navigation tracking
        extensions.push(reactPlugin)
        extensionConfig[reactPlugin.identifier] = { history }
      }
    }

    AppInsightsInstance = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: enableNavigationTracking,
        extensions,
        extensionConfig
      }
    })

    AppInsightsInstance.loadAppInsights()
    AppInsightsInstance.context.application.ver = appVersion
    AppInsightsInstance.context.application.build = buildNumber

    // Stamp the build onto every event/exception's customDimensions so it's queryable
    // as a column in App Insights. context.application.build above sets the SDK
    // envelope's application.build tag, but that doesn't reliably surface in
    // customEvents queries — customDimensions does. Lets us distinguish bundles in
    // production (e.g. when a refactor changes event shapes and cached old bundles
    // linger after a deploy).
    //
    // In @microsoft/applicationinsights-web's ITelemetryItem, custom dimensions live
    // directly on envelope.data — NOT under envelope.data.baseData.properties (an
    // earlier version of this initializer wrote there and silently produced no
    // customDimensions in queries). Spread the existing values, then set `build` last
    // so it overrides any accidental per-call value.
    AppInsightsInstance.addTelemetryInitializer((envelope) => {
      envelope.data = { ...(envelope.data || {}), build: buildNumber }
    })
  }
}

const getAppInsightsInstance = () => AppInsightsInstance

export const logError = (message, error, properties = {}) => {
  if (!message && !error) return

  if (!AppInsightsInstance) {
    console.log('AppInsightsInstance not initialized')
    return
  }

  console.error(message, error)
  AppInsightsInstance.trackException({
    exception: error || new Error(message),
    properties: { ospreyMessage: message || 'No error message found', ...properties }
  })
}

// non-error telemetry — use for transient/recoverable conditions and recovery events
// (failures that retry past or successes that follow a retry). distinct from logError so
// these don't pollute the exceptions table.
export const logEvent = (name, properties = {}) => {
  if (!name) return

  if (!AppInsightsInstance) {
    console.log('AppInsightsInstance not initialized')
    return
  }

  AppInsightsInstance.trackEvent({ name, properties })
}

export default init
export { getAppInsightsInstance }
